<template>
  <div class="home-page">
    <el-container>
      <el-header class="header">
        <div></div>
        <div class="profile">
          <el-dropdown trigger="click">
            <span class="el-dropdown-link">
              {{ userInfo.userName }}
              <span
                v-if="roleDisplayName"
                class="role-text"
                :title="roleDisplayName"
              >
                （{{ roleDisplayName }}）
              </span>
              <i class="el-icon-arrow-down el-icon--right"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item>
                <a @click.prevent="showChangePassword" class="exit">修改密码</a>
              </el-dropdown-item>
              <el-dropdown-item>
                <a @click.prevent="logout" class="exit">退出</a>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <el-dialog
            title="修改密码"
            :visible.sync="changePasswordVisible"
            width="420px"
            :close-on-click-modal="false"
            @close="closeChangePasswordDialog"
          >
            <el-form label-width="110px">
              <el-form-item label="旧密码">
                <el-input
                  v-model.trim="changePasswordForm.oldPassword"
                  type="password"
                  show-password
                  autocomplete="current-password"
                />
              </el-form-item>
              <el-form-item label="新密码">
                <el-input
                  v-model.trim="changePasswordForm.newPassword"
                  type="password"
                  show-password
                  autocomplete="new-password"
                />
              </el-form-item>
              <el-form-item label="确认新密码">
                <el-input
                  v-model.trim="changePasswordForm.confirmPassword"
                  type="password"
                  show-password
                  autocomplete="new-password"
                />
              </el-form-item>
              <div class="password-tip">
                密码至少8位，且必须包含数字、大写字母、小写字母和符号
              </div>
            </el-form>
            <div slot="footer">
              <el-button @click="closeChangePasswordDialog">取消</el-button>
              <el-button type="primary" @click="submitChangePassword">确定</el-button>
            </div>
          </el-dialog>
          <el-dialog
            title="谷歌验证码"
            :visible.sync="googleDialogVisible"
            width="320px"
            :close-on-click-modal="false"
            @close="closeGoogleDialog"
          >
            <el-form label-width="100px">
              <el-form-item label="谷歌验证码">
                <el-input v-model.trim="googleForm.googleCode" autocomplete="off" />
              </el-form-item>
            </el-form>
            <div slot="footer">
              <el-button @click="closeGoogleDialog">取消</el-button>
              <el-button type="primary" @click="confirmChangePassword">确定</el-button>
            </div>
          </el-dialog>
        </div>
      </el-header>
      <el-container style="height: 100%">
        <el-aside class="aside" width="220px" height="100%">
          <el-menu :router="true" :default-active="$route.path">
            <DynamicMenuTree :nodes="menuTree" />
          </el-menu>
        </el-aside>
        <el-main>
          <router-view></router-view>
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import DynamicMenuTree from "@/components/DynamicMenuTree.vue";
import http from "./plugins/axios";
import encrypt from "@/utils/encrypt";

const HIDDEN_MENU_PREFIXES = [
  "/home/otc",
  "/home/otcBill",
  "/home/otctBill",
  "/home/audit/otc",
];

function shouldHideMenu(pathUrl = "") {
  return HIDDEN_MENU_PREFIXES.some(
    (prefix) => pathUrl === prefix || pathUrl.startsWith(`${prefix}/`)
  );
}

function filterMenuTree(nodes = []) {
  return (Array.isArray(nodes) ? nodes : [])
    .map((node) => ({
      ...node,
      children: filterMenuTree(node.children || []),
    }))
    .filter((node) => {
      if (shouldHideMenu(node.pathUrl || "")) {
        return false;
      }
      return !!(node.pathUrl || (node.children && node.children.length));
    });
}

export default {
  name: "HomePage",
  components: {
    DynamicMenuTree,
  },
  data() {
    return {
      changePasswordVisible: false,
      googleDialogVisible: false,
      changePasswordForm: {
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
      googleForm: {
        googleCode: "",
      },
    };
  },
  computed: {
    ...mapState(["userInfo"]),
    menuTree() {
      return filterMenuTree(this.userInfo.menuTree || []);
    },
    roleDisplayName() {
      const roleNames = this.userInfo.roleNames || [];
      return roleNames.filter(Boolean).join(" / ");
    },
  },
  methods: {
    ...mapActions(["logout"]),
    showChangePassword() {
      this.changePasswordVisible = true;
    },
    closeChangePasswordDialog() {
      this.changePasswordVisible = false;
      this.changePasswordForm = {
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      };
    },
    closeGoogleDialog() {
      this.googleDialogVisible = false;
      this.googleForm = {
        googleCode: "",
      };
    },
    validatePasswordRule(password) {
      return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/.test(
        password
      );
    },
    submitChangePassword() {
      const { oldPassword, newPassword, confirmPassword } =
        this.changePasswordForm;
      if (!oldPassword) {
        this.$message.error("请输入旧密码");
        return;
      }
      if (!newPassword) {
        this.$message.error("请输入新密码");
        return;
      }
      if (!this.validatePasswordRule(newPassword)) {
        this.$message.error(
          "新密码至少8位，且必须包含数字、大写字母、小写字母和符号"
        );
        return;
      }
      if (!confirmPassword) {
        this.$message.error("请再次输入新密码");
        return;
      }
      if (newPassword !== confirmPassword) {
        this.$message.error("两次输入的新密码不一致");
        return;
      }
      if (oldPassword === newPassword) {
        this.$message.error("新密码不能与旧密码相同");
        return;
      }
      this.googleDialogVisible = true;
    },
    confirmChangePassword() {
      if (!this.googleForm.googleCode) {
        this.$message.error("请输入谷歌验证码");
        return;
      }
      const { oldPassword, newPassword } = this.changePasswordForm;
      http
        .post({
          url: "/ta/admin/tenant/password/change",
          data: {
            oldPassword: encrypt(oldPassword),
            newPassword: encrypt(newPassword),
            googleCode: this.googleForm.googleCode,
          },
        })
        .then((res) => {
          if (res.code === 0) {
            this.$message.success("修改密码成功，请重新登录");
            this.closeGoogleDialog();
            this.closeChangePasswordDialog();
            setTimeout(() => {
              this.logout();
            }, 300);
          }
        });
    },
  },
};
</script>

<style lang="less" scoped>
.home-page {
  display: flex;
  height: 100%;
  background-color: #f5f7fa;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #dcdfe6;
  background-color: #fff;
}

.role-text {
  color: #909399;
  font-size: 12px;
  margin-left: 4px;
  display: inline-block;
  max-width: 220px;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.aside {
  background-color: #fff;
  border-right: 1px solid #dcdfe6;
  .el-menu {
    border: 0;
  }
}

.exit {
  width: 76px;
  display: block;
  text-align: center;
  height: 100%;
}

.password-tip {
  color: #909399;
  line-height: 20px;
  padding-left: 110px;
}
</style>
